/**
 * BRSR Core attribute catalogue summarised from Annexure I of SEBI circular
 * SEBI/HO/CFD/CFD-SEC-2/P/CIR/2023/122 (12 July 2023).
 *
 * The March 2025 circular (SEBI/HO/CFD/CFD-PoD-1/P/CIR/2025/42) restates BRSR Core
 * as a set of KPIs under nine ESG attributes and points to Annexure 17A of the
 * master circular for the assessment or assurance format. It did not, in the text
 * reviewed, replace this attribute list. Parameter wording should be checked
 * against the current master-circular annexure before assurance fieldwork.
 */

export type CoreCatalogueStatus = 'summarised_requires_annexure_check'

export interface BrsrCoreMetric {
  id: string
  attribute: string
  parameter: string
  brsrCrossReference: string
  group: 'environment' | 'social' | 'governance'
}

export const BRSR_CORE_CATALOGUE_STATUS: CoreCatalogueStatus = 'summarised_requires_annexure_check'

export const BRSR_CORE_METRICS: BrsrCoreMetric[] = [
  {
    id: 'ghg-scope1',
    attribute: 'Green-house gas (GHG) footprint',
    parameter: 'Total Scope 1 emissions',
    brsrCrossReference: 'Principle 6, Question 7, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'ghg-scope2',
    attribute: 'Green-house gas (GHG) footprint',
    parameter: 'Total Scope 2 emissions',
    brsrCrossReference: 'Principle 6, Question 7, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'ghg-intensity',
    attribute: 'Green-house gas (GHG) footprint',
    parameter: 'GHG emission intensity (Scope 1 and 2), including revenue adjusted for PPP',
    brsrCrossReference: 'Principle 6, Question 7, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'water-consumption',
    attribute: 'Water footprint',
    parameter: 'Total water consumption',
    brsrCrossReference: 'Principle 6, Question 3, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'water-intensity',
    attribute: 'Water footprint',
    parameter: 'Water consumption intensity, including PPP-adjusted turnover',
    brsrCrossReference: 'Principle 6, Question 3, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'water-discharge',
    attribute: 'Water footprint',
    parameter: 'Water discharge by destination and level of treatment',
    brsrCrossReference: 'Principle 6, Question 4, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'energy-total',
    attribute: 'Energy footprint',
    parameter: 'Total energy consumed',
    brsrCrossReference: 'Principle 6, Question 1, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'energy-renewable',
    attribute: 'Energy footprint',
    parameter: 'Percentage of energy consumed from renewable sources',
    brsrCrossReference: 'Principle 6, Question 1, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'energy-intensity',
    attribute: 'Energy footprint',
    parameter: 'Energy intensity, including PPP-adjusted turnover',
    brsrCrossReference: 'Principle 6, Question 1, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'waste-categories',
    attribute: 'Embracing circularity — waste management',
    parameter:
      'Waste by category (plastic, e-waste, bio-medical, construction and demolition, battery, radioactive, other hazardous and other non-hazardous), total waste and intensity',
    brsrCrossReference: 'Principle 6, Question 9, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'waste-recovery-disposal',
    attribute: 'Embracing circularity — waste management',
    parameter: 'Waste recovered and waste disposed, by category and method',
    brsrCrossReference: 'Principle 6, Question 9, Essential Indicators',
    group: 'environment',
  },
  {
    id: 'employee-wellbeing-spend',
    attribute: 'Enhancing employee wellbeing and safety',
    parameter: 'Spending on measures towards well-being of employees and workers as a percentage of revenue',
    brsrCrossReference: 'Principle 3, Question 1(c), Essential Indicators',
    group: 'social',
  },
  {
    id: 'safety-incidents',
    attribute: 'Enhancing employee wellbeing and safety',
    parameter: 'Safety-related incidents for employees and workers, including LTIFR and fatalities',
    brsrCrossReference: 'Principle 3, Question 11, Essential Indicators',
    group: 'social',
  },
  {
    id: 'wages-women',
    attribute: 'Enabling gender diversity in business',
    parameter: 'Gross wages paid to females as a percentage of wages paid',
    brsrCrossReference: 'Principle 5, Question 3(b), Essential Indicators',
    group: 'social',
  },
  {
    id: 'posh',
    attribute: 'Enabling gender diversity in business',
    parameter: 'Complaints on POSH, including complaints as a percentage of female employees and workers',
    brsrCrossReference: 'Principle 5, Question 7, Essential Indicators',
    group: 'social',
  },
  {
    id: 'msme-sourcing',
    attribute: 'Enabling inclusive development',
    parameter: 'Input material sourced directly from MSMEs or small producers and from within India, as a percentage of purchases',
    brsrCrossReference: 'Principle 8, Question 4, Essential Indicators',
    group: 'social',
  },
  {
    id: 'job-creation-towns',
    attribute: 'Enabling inclusive development',
    parameter: 'Wages paid to persons employed in smaller towns as a percentage of total wage cost',
    brsrCrossReference: 'Principle 8, Question 5, Essential Indicators',
    group: 'social',
  },
  {
    id: 'data-breaches',
    attribute: 'Fairness in engaging with customers and suppliers',
    parameter: 'Instances involving loss or breach of data of customers as a percentage of total data breaches or cyber security events',
    brsrCrossReference: 'Principle 9, Question 7, Essential Indicators',
    group: 'governance',
  },
  {
    id: 'accounts-payable',
    attribute: 'Fairness in engaging with customers and suppliers',
    parameter: 'Number of days of accounts payable',
    brsrCrossReference: 'Principle 1, Question 8, Essential Indicators',
    group: 'governance',
  },
  {
    id: 'openness-concentration',
    attribute: 'Open-ness of business',
    parameter: 'Concentration of purchases and sales with trading houses, dealers and related parties',
    brsrCrossReference: 'Principle 1, Question 9, Essential Indicators',
    group: 'governance',
  },
  {
    id: 'openness-rpt',
    attribute: 'Open-ness of business',
    parameter: 'Loans and advances and investments with related parties',
    brsrCrossReference: 'Principle 1, Question 9, Essential Indicators',
    group: 'governance',
  },
]

export function coreMetricsForGroup(group: BrsrCoreMetric['group']): BrsrCoreMetric[] {
  return BRSR_CORE_METRICS.filter((metric) => metric.group === group)
}

export function coreAttributeSummary(group: BrsrCoreMetric['group']): string {
  const names = [...new Set(coreMetricsForGroup(group).map((metric) => metric.attribute))]
  return names.join('; ')
}
